import { PopoverContent, PopoverRoot, PopoverTrigger } from '@crow/ui';
import type { EventTypeView } from '@crowdex/agent-contracts/api';
import { Chip } from '@crowdex/ui/components/chip';
import { cn } from '@crowdex/ui/lib/utils';
import { AppWindow, ArrowLeft, Bell, type LucideIcon, Settings } from 'lucide-react';
import { type ReactElement, useMemo, useRef, useState } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { afterLayers } from '@/components/mobile-shell/use-mobile-layers';
import { useShellNavigation } from '@/components/sidebar/shell-navigation';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { PREVIEW_APPS } from '@/lib/desktop-shell/preview-shell-data';
import { useRouter } from '@/lib/navigation';
import { CONNECTIONS_PATH, connectionPagePath } from '@/lib/plugins/plugin-sections';
import { CrowdexGlyph } from './crowdex-glyph';
import type { FeedNotification } from './notification-feed';
import { useNotifications } from './notifications';

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/** "3 minutes ago", "yesterday"; "just now" under a minute. */
export function relativeTime(iso: string, now = Date.now()): string {
  const elapsed = now - Date.parse(iso);
  const format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (elapsed >= size) return format.format(-Math.floor(elapsed / size), unit);
  }
  return 'just now';
}

/** The unread count of the rail's bell; "9+" past nine. */
function unreadBadge(unread: number): string | null {
  if (unread === 0) return null;
  return unread > 9 ? '9+' : String(unread);
}

/** Who published a notification, as its filter pill names it: Crowdex, or its plugin. */
export function notificationSource(
  notification: Pick<FeedNotification, 'plugin'>,
  names: Readonly<Record<string, string>>,
): string {
  if (notification.plugin === null) return 'Crowdex';
  return (
    names[notification.plugin] ??
    notification.plugin
      .split('.')
      .at(-1)
      ?.replace(/^./, (letter) => letter.toUpperCase()) ??
    notification.plugin
  );
}

/** Its time on the row: "20 min", "2 h" today, the hour before. */
export function notificationTime(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  const elapsed = Math.max(0, now - at);
  if (sameDay(at, now)) {
    if (elapsed < 60_000) return 'now';
    if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)} min`;
    return `${Math.floor(elapsed / 3_600_000)} h`;
  }
  return new Date(at).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
}

function sameDay(left: number, right: number): boolean {
  return new Date(left).toDateString() === new Date(right).toDateString();
}

/** "Today", "Yesterday", else the date: how the feed groups its notifications. */
export function notificationDay(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (sameDay(at, now)) return 'Today';
  // The calendar's day before, not 24 hours before: a day when the clocks change is 23 or 25 h.
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(at, yesterday.getTime())) return 'Yesterday';
  return new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
}

/**
 * What a row's button does beside opening it (the row itself opens what it is about): sign an
 * account in again, see the run, review, download, call back.
 */
export type NotificationAction = {
  label: string;
  kind: 'open' | 'reconnect' | 'download';
  /** Where `reconnect` and `download` lead. */
  href?: string;
};

function stringField(notification: FeedNotification, name: string): string | null {
  const value = notification.fields[name];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Whether a row opens something (its conversation, its app's tab, its page): the row is a button. */
function notificationOpens(
  notification: Pick<FeedNotification, 'threadId' | 'url' | 'link'>,
): boolean {
  return notification.threadId !== null || notification.url !== null || notification.link !== null;
}

/**
 * The actions on a row (D4, mobile 46), said for what it is about, from the data the event
 * carries: Reconnect <service> (`fields.reconnect`, its account `fields.connection`), See the run
 * (`fields.run`), Review (a warning), Download (`fields.download`, a URL). A missed call is called
 * back. Plain opening is the row's own click, never a pill. Read or not: reading a row does not
 * do what it asks.
 */
export function notificationActions(notification: FeedNotification): NotificationAction[] {
  if (notification.type === 'crowdex/assistant.ring') return [{ label: 'Call back', kind: 'open' }];
  const actions: NotificationAction[] = [];
  const service = stringField(notification, 'reconnect');
  if (service !== null) {
    const connection = stringField(notification, 'connection');
    actions.push({
      label: `Reconnect ${service}`,
      kind: 'reconnect',
      href: connection === null ? CONNECTIONS_PATH : connectionPagePath(connection),
    });
  }
  if (notificationOpens(notification)) {
    if (notification.severity === 'warning') actions.push({ label: 'Review', kind: 'open' });
    else if (stringField(notification, 'run') !== null)
      actions.push({ label: 'See the run', kind: 'open' });
  }
  const download = stringField(notification, 'download');
  if (download !== null && /^(https:\/\/|\/[^/])/.test(download))
    actions.push({ label: 'Download', kind: 'download', href: download });
  return actions;
}

/** What a row is shown by: Crowdex's glyph for the core's events, else its app's. */
export type NotificationGlyph = 'crowdex' | { icon: LucideIcon; src?: string | undefined };

/**
 * The glyph of who published a notification: its app's icon (the core paints it in ink),
 * Crowdex's own glyph for the core's; never an initial. A preview's apps are the boards'.
 */
export function notificationGlyph(
  notification: Pick<FeedNotification, 'plugin'>,
  panels: readonly Pick<PluginPanel, 'plugin' | 'icon' | 'iconSrc'>[],
  preview: boolean,
): NotificationGlyph {
  const plugin = notification.plugin;
  if (plugin === null) return 'crowdex';
  if (preview) {
    const app = PREVIEW_APPS.find((entry) => entry.view === `preview:${plugin.split('.').at(-1)}`);
    if (app !== undefined) return { icon: app.icon };
  }
  const panel = panels.find((entry) => entry.plugin === plugin);
  if (panel === undefined) return { icon: AppWindow };
  return { icon: pluginIcon(panel.icon ?? 'app'), src: panel.iconSrc };
}

/**
 * What a row says of its type: "Waits on you" (amber) for a type that waits, else the type's
 * title; never its id (`run.failed`), which only its tooltip shows. Nothing while it is unknown.
 */
function notificationKind(
  notification: Pick<FeedNotification, 'type'>,
  types: ReadonlyMap<string, Pick<EventTypeView, 'title' | 'waits'>>,
): { label: string; waits: boolean } | null {
  const type = types.get(notification.type);
  if (type === undefined) return null;
  return type.waits ? { label: 'Waits on you', waits: true } : { label: type.title, waits: false };
}

/**
 * The state dot on a row's glyph, or none: amber for what waits on the user, red for a failure,
 * green for a success. A plain reply, news and a call carry none (unread is its own neutral mark).
 */
export function notificationDot(
  notification: Pick<FeedNotification, 'type' | 'severity'>,
  kind: { waits: boolean } | null,
): 'waits' | 'failed' | 'succeeded' | null {
  if (kind?.waits === true) return 'waits';
  if (notification.type === 'crowdex/agent.replied') return null;
  if (notification.severity === 'error') return 'failed';
  if (notification.severity === 'success') return 'succeeded';
  return null;
}

const DOT_CLASS = { waits: 'bg-attention', failed: 'bg-destructive', succeeded: 'bg-success' };

/** One row of the feed: a notification and the earlier ones of its conversation that day. */
export type NotificationGroup = {
  /** The latest, which the row shows and opens. */
  latest: FeedNotification;
  /** Every one it stands for, newest first (the latest included). */
  entries: FeedNotification[];
};

/**
 * One row per conversation per day (its latest notification, the earlier ones counted); the
 * others one row each. `list` is newest first, as the feed lists it.
 */
export function groupNotifications(
  list: readonly FeedNotification[],
  now = Date.now(),
): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  const byConversation = new Map<string, NotificationGroup>();
  for (const notification of list) {
    if (notification.threadId === null) {
      groups.push({ latest: notification, entries: [notification] });
      continue;
    }
    const day = notificationDay(notification.createdAt, now);
    const key = `${notification.source}:${notification.threadId}:${day}`;
    const group = byConversation.get(key);
    if (group !== undefined) group.entries.push(notification);
    else {
      const created = { latest: notification, entries: [notification] };
      byConversation.set(key, created);
      groups.push(created);
    }
  }
  return groups;
}

/** How many a row stands for: "3 replies" when they all are, else "+2"; nothing for one. */
export function groupCount(group: NotificationGroup): string | null {
  const count = group.entries.length;
  if (count < 2) return null;
  return group.entries.every((entry) => entry.type === 'crowdex/agent.replied')
    ? `${count} replies`
    : `+${count - 1}`;
}

/**
 * The line under a row's title: the app that published it when it is not Crowdex (its type in
 * the tooltip), "Waits on you" in amber for what does, how many the row stands for, its excerpt.
 * The core's source and type ("Crowdex · An agent replied") say nothing the title does not.
 */
function notificationMeta(
  group: NotificationGroup,
  source: string,
  kind: ReturnType<typeof notificationKind>,
): { key: string; text: string; className?: string; title?: string }[] {
  const { latest } = group;
  const count = groupCount(group);
  return [
    ...(latest.plugin === null
      ? []
      : [
          {
            key: 'source',
            text: source,
            title: kind === null || kind.waits ? latest.type : `${kind.label} (${latest.type})`,
          },
        ]),
    ...(kind?.waits === true
      ? [{ key: 'kind', text: kind.label, title: latest.type, className: 'text-attention' }]
      : []),
    ...(count === null ? [] : [{ key: 'count', text: count }]),
    ...(latest.body ? [{ key: 'body', text: latest.body }] : []),
  ];
}

function NotificationRow({
  group,
  source,
  kind,
  glyph,
  onOpen,
  onAction,
}: {
  group: NotificationGroup;
  source: string;
  kind: ReturnType<typeof notificationKind>;
  glyph: NotificationGlyph;
  onOpen: () => void;
  onAction: (action: NotificationAction) => void;
}) {
  const notification = group.latest;
  // What waits on the user is no unread news: it says "Waits on you" until answered.
  const unread = kind?.waits !== true && group.entries.some((entry) => entry.readAt === null);
  const actions = notificationActions(notification);
  const meta = notificationMeta(group, source, kind);
  const dot = notificationDot(notification, kind);
  const opens = notificationOpens(notification);
  // Outline pills above the row's own button: the screen's one filled button is never a row's.
  const pill =
    'type-caption relative z-10 inline-flex h-7 items-center rounded-control border border-hairline-strong px-3 font-medium text-foreground hover:bg-control';
  const text = (
    <>
      <span className="flex items-baseline gap-2">
        <span
          className={cn(
            'type-ui min-w-0 flex-1 font-semibold',
            unread ? 'text-foreground' : 'text-foreground/80',
          )}
        >
          {notification.title}
        </span>
        <span className="type-caption shrink-0 text-muted-foreground tabular-nums">
          {notificationTime(notification.createdAt)}
        </span>
      </span>
      {meta.length > 0 && (
        <span className="type-caption line-clamp-2 text-muted-foreground">
          {meta.map((part, index) => (
            <span key={part.key}>
              {index > 0 && ' · '}
              <span className={part.className} title={part.title}>
                {part.text}
              </span>
            </span>
          ))}
        </span>
      )}
    </>
  );
  return (
    <li
      className={cn(
        'relative flex gap-3 rounded-xl py-2.5 pr-2 pl-4',
        opens && 'hover:bg-control/50',
      )}
      data-notification-row
    >
      {unread && (
        // Neutral: amber is only for what waits on the user.
        <span className="absolute top-[18px] left-1 size-1.5 rounded-full bg-foreground">
          <span className="sr-only">Unread</span>
        </span>
      )}
      <span className="relative mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-control text-foreground/80">
        {glyph === 'crowdex' ? (
          <CrowdexGlyph className="size-3.5" />
        ) : (
          <ItemIcon className="size-3.5" icon={glyph.icon} src={glyph.src} />
        )}
        {dot !== null && (
          <span
            aria-hidden
            className={cn(
              'absolute -right-0.5 -bottom-0.5 size-2 rounded-full ring-2 ring-crow-surface',
              DOT_CLASS[dot],
            )}
            data-notification-dot={dot}
          />
        )}
      </span>
      <div className="min-w-0 flex-1">
        {opens ? (
          // The whole row opens it: its button stretches over the row, its pills sit above.
          <button
            className="block w-full rounded-md text-left after:absolute after:inset-0 after:rounded-xl"
            onClick={onOpen}
            type="button"
          >
            {text}
          </button>
        ) : (
          <div>{text}</div>
        )}
        {actions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {actions.map((action) =>
              action.kind === 'download' ? (
                <a
                  className={pill}
                  download
                  href={action.href}
                  key={action.label}
                  onClick={() => onAction(action)}
                  rel="noopener"
                  target="_blank"
                >
                  {action.label}
                </a>
              ) : (
                <button
                  className={pill}
                  key={action.label}
                  onClick={() => onAction(action)}
                  type="button"
                >
                  {action.label}
                </button>
              ),
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Where what a conversation waits on stays: the sidebar on a desktop (D4), the new conversation
 * screen on a phone (46).
 */
export function notificationsFootnote(density: 'desktop' | 'phone'): string {
  const where = density === 'desktop' ? 'in the sidebar' : 'on the new conversation screen';
  return `What a conversation waits on stays ${where} until you answer it: reading it here does not.`;
}

/**
 * One feed of every plugin's events (D4, mobile 46): filter pills per publisher with their
 * unread count, "Mark all read", grouped by day, each row's action on it.
 */
export function NotificationsFeed({
  onOpened,
  density = 'desktop',
  onBack,
}: {
  onOpened: () => void;
  density?: 'desktop' | 'phone';
  /** A phone's screen goes back with its arrow. */
  onBack?: () => void;
}) {
  const notifications = useNotifications();
  const workbench = useWorkbench();
  const { pluginDisplayNames } = workbench;
  const panels = useMemo(() => railPanels(workbench), [workbench]);
  const { preview } = useShellNavigation();
  const router = useRouter();
  const [filter, setFilter] = useState('all');
  // A page goes once the feed is left: on a phone, once its screen's history entries are gone.
  const leaveFor = (href: string) => {
    onOpened();
    afterLayers(() => router.push(href));
  };
  // What a conversation waits on is in Needs you (the sidebar, the drawer), not here; what
  // waits on the user outside a conversation stays listed.
  const list = useMemo(
    () =>
      (notifications?.notifications ?? []).filter(
        (notification) =>
          notification.threadId === null || !notifications?.waitingTypes.has(notification.type),
      ),
    [notifications?.notifications, notifications?.waitingTypes],
  );
  const sources = useMemo(() => {
    const counts = new Map<string, number>();
    for (const notification of list) {
      const name = notificationSource(notification, pluginDisplayNames);
      counts.set(name, (counts.get(name) ?? 0) + (notification.readAt === null ? 1 : 0));
    }
    // Crowdex first, then the plugins by name.
    return [...counts].sort(([left], [right]) =>
      left === 'Crowdex' ? -1 : right === 'Crowdex' ? 1 : left.localeCompare(right),
    );
  }, [list, pluginDisplayNames]);
  if (notifications === null) return null;
  const shown = list.filter(
    (notification) =>
      filter === 'all' || notificationSource(notification, pluginDisplayNames) === filter,
  );
  const days: [string, NotificationGroup[]][] = [];
  for (const group of groupNotifications(shown)) {
    const day = notificationDay(group.latest.createdAt);
    const last = days.at(-1);
    if (last?.[0] === day) last[1].push(group);
    else days.push([day, [group]]);
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Its header stays while the list scrolls: title, Mark all read, settings, filters. */}
      <div className="shrink-0" data-notifications-header>
        <div
          className={cn(
            'flex items-center gap-2',
            density === 'desktop' ? 'px-4 pt-3' : 'px-2 pt-2',
          )}
        >
          {onBack !== undefined && (
            <button
              aria-label="Back"
              className="flex size-10 items-center justify-center rounded-control active:bg-accent/60"
              onClick={onBack}
              type="button"
            >
              <ArrowLeft className="size-5" />
            </button>
          )}
          <h2
            className={cn(
              'flex-1 font-semibold',
              density === 'desktop' ? 'type-card-title' : 'type-page-title',
            )}
          >
            Notifications
          </h2>
          {notifications.unread > 0 && (
            <button
              className={cn(
                'type-ui rounded-sm px-1.5',
                density === 'desktop' ? 'text-foreground hover:text-foreground/80' : 'text-running',
              )}
              onClick={notifications.markAllRead}
              type="button"
            >
              Mark all read
            </button>
          )}
          <button
            aria-label="Notification settings"
            className={cn(
              'flex items-center justify-center rounded-control text-muted-foreground',
              density === 'desktop'
                ? 'size-7 hover:bg-control hover:text-foreground'
                : 'size-10 active:bg-accent/60',
            )}
            onClick={() => leaveFor('/settings?section=notifications')}
            type="button"
          >
            <Settings className={density === 'desktop' ? 'size-3.5' : 'size-5'} />
          </button>
        </div>
        {sources.length > 0 && (
          <div
            className="flex shrink-0 gap-1.5 overflow-x-auto px-4 py-2.5"
            data-notifications-filters
          >
            {[['all', 0] as const, ...sources].map(([name, unread]) => (
              // The chosen source: the shared selected state, never filled (one filled button
              // per screen, and it is not a filter).
              <Chip
                className="type-ui"
                key={name}
                onClick={() => setFilter(name)}
                selected={filter === name}
                size="md"
              >
                {name === 'all' ? 'All' : name}
                {unread > 0 && <span className="text-muted-foreground">{unread}</span>}
              </Chip>
            ))}
          </div>
        )}
      </div>
      {shown.length === 0 && notifications.loadFailed ? (
        <div
          className="type-ui flex items-center justify-center gap-2 px-4 py-8 text-muted-foreground"
          role="alert"
        >
          <span>Couldn't load notifications</span>
          <span aria-hidden>·</span>
          <button
            className="font-medium text-foreground underline-offset-4 hover:underline"
            onClick={() => void notifications.refresh()}
            type="button"
          >
            Retry
          </button>
        </div>
      ) : shown.length === 0 ? (
        <p className="type-ui px-4 py-8 text-center text-muted-foreground">
          No notifications yet. You are told here when an agent replies or waits on you.
        </p>
      ) : (
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-3',
            // Full screen on a phone: its end clears the home indicator.
            density === 'desktop' ? 'pb-2' : 'pb-[max(0.5rem,env(safe-area-inset-bottom))]',
          )}
          data-notifications-list
        >
          {days.map(([day, groups]) => (
            <section aria-label={day} key={day}>
              <h3 className="type-caption px-1 pt-2 pb-0.5 text-muted-foreground">{day}</h3>
              <ul>
                {groups.map((group) => {
                  const notification = group.latest;
                  return (
                    <NotificationRow
                      glyph={notificationGlyph(notification, panels, preview)}
                      group={group}
                      key={`${notification.source}:${notification.id}`}
                      kind={notificationKind(notification, notifications.types)}
                      onAction={(action) => {
                        if (action.kind === 'download') {
                          notifications.markRead([notification]);
                          return;
                        }
                        if (action.kind === 'open') {
                          onOpened();
                          notifications.open(notification);
                        } else {
                          notifications.markRead([notification]);
                          leaveFor(action.href ?? CONNECTIONS_PATH);
                        }
                      }}
                      onOpen={() => {
                        onOpened();
                        notifications.open(notification);
                        // Its conversation's earlier ones that day are read with it.
                        if (group.entries.length > 1) notifications.markRead(group.entries);
                      }}
                      source={notificationSource(notification, pluginDisplayNames)}
                    />
                  );
                })}
              </ul>
            </section>
          ))}
          <p className="type-caption border-hairline border-t px-1 pt-2.5 pb-1 text-muted-foreground">
            {notificationsFootnote(density)}
          </p>
        </div>
      )}
    </div>
  );
}

/** The notifications in a panel below the element that opens them (the sidebar's bell). */
function NotificationsPopover({
  children,
  side = 'bottom',
}: {
  children: ReactElement;
  side?: 'bottom' | 'right';
}) {
  const notifications = useNotifications();
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  if (notifications === null) return null;
  return (
    <PopoverRoot onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/* An anchored panel like every other (the product switcher's): no backdrop, the window
          stays as it is; a click outside or Escape closes it. */}
      <PopoverContent
        variant="panel"
        aria-label="Notifications"
        // Under its bell, inside the window however narrow or short it is.
        className="flex max-h-[min(44rem,85dvh,var(--radix-popover-content-available-height))] w-[min(440px,calc(100vw-1rem))] flex-col overflow-hidden"
        collisionPadding={8}
        data-programmatic-focus
        // Opening focuses the panel itself, not its first control: no ring until the keyboard
        // moves on (each control then draws the shared one).
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          panel.current?.focus({ preventScroll: true });
        }}
        ref={panel}
        side={side}
        sideOffset={8}
        tabIndex={-1}
      >
        <NotificationsFeed onOpened={() => setOpen(false)} />
      </PopoverContent>
    </PopoverRoot>
  );
}

/**
 * The bell (D1): a dot while something is unread (neutral: amber is what waits on the user).
 * In the rail (D2) it shows the count.
 */
export function NotificationsButton({
  className,
  variant = 'dot',
}: {
  className?: string;
  variant?: 'dot' | 'count';
}) {
  const notifications = useNotifications();
  if (notifications === null) return null;
  const badge = unreadBadge(notifications.unread);
  return (
    <NotificationsPopover side={variant === 'count' ? 'right' : 'bottom'}>
      <button
        aria-label={
          badge === null ? 'Notifications' : `Notifications, ${notifications.unread} unread`
        }
        className={cn(
          'relative flex size-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-control hover:text-foreground data-[state=open]:bg-control data-[state=open]:text-foreground',
          className,
        )}
        title="Notifications"
        type="button"
      >
        <Bell className="size-4" />
        {badge !== null &&
          (variant === 'dot' ? (
            <span className="absolute top-1 right-1 size-1.5 rounded-full bg-foreground" />
          ) : (
            <span className="type-caption absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 font-semibold text-background leading-none">
              {badge}
            </span>
          ))}
      </button>
    </NotificationsPopover>
  );
}
